import type { EmployeeSnapshot } from '@klukka/client';
import { dow, fmt, fmtDay, fmtH, t, type DayRow, type LangCode } from '@klukka/core';
import { Ic } from '@klukka/ui';
import type { EmployeeView } from '../derive';
import { PeriodNav } from '../PeriodNav';

/** A row in the day list: either a worked day or a collapsed run of empty ones. */
type Item =
  | { kind: 'day'; row: DayRow; adjustments: EmployeeSnapshot['adjustments'] }
  | { kind: 'gap'; span: string; label: string };

/**
 * Collapse consecutive empty weekends and holidays into one slim line, so the
 * list is dominated by days that actually earned something.
 */
function buildItems(
  rows: DayRow[],
  adjustments: EmployeeSnapshot['adjustments'],
  labels: { weekend: string; holiday: string },
): Item[] {
  const out: Item[] = [];
  let run: DayRow[] = [];

  const flush = () => {
    if (!run.length) return;
    const newest = run[0];
    const oldest = run[run.length - 1];
    const parts = [
      run.some((x) => x.weekend) ? labels.weekend : null,
      run.some((x) => x.holiday) ? labels.holiday : null,
    ].filter(Boolean);
    out.push({
      kind: 'gap',
      span: fmtDay(oldest.date) + (oldest.date !== newest.date ? `–${fmtDay(newest.date)}` : ''),
      label: parts.join(' / ') || '—',
    });
    run = [];
  };

  for (const row of rows.slice().reverse()) {
    const dayAdjustments = adjustments.filter((a) => a.date === row.date);
    if (!row.h && !row.sick && !row.vacation && !dayAdjustments.length) {
      run.push(row);
      continue;
    }
    flush();
    out.push({ kind: 'day', row, adjustments: dayAdjustments });
  }
  flush();
  return out;
}

export function PeriodTab({ snap, view, lang, offset, onOffset, onEditDay }: {
  snap: EmployeeSnapshot;
  view: EmployeeView;
  lang: LangCode;
  offset: number;
  onOffset: (n: number) => void;
  onEditDay: (row: DayRow) => void;
}) {
  const tr = (k: Parameters<typeof t>[1]) => t(lang, k);
  const { sel, expected, workedHours } = view;
  const calc = sel.calc;
  const settings = snap.company.settings;
  const grant = snap.grant;

  const canEdit = (r: DayRow) =>
    !!grant && !r.isToday && r.date >= grant.from && r.date <= grant.to && !r.sick && !r.vacation;

  const items = buildItems(calc.rows, snap.adjustments, { weekend: tr('weekend'), holiday: tr('holiday') });

  return (
    <div className="ph-body">
      <PeriodNav pinfo={sel.pinfo} offset={offset} onOffset={onOffset} />

      {grant ? (
        <div className="grant-banner">
          <Ic.unlock size={15} /> {tr('editAllowed')} · {tr('openUntil')} {fmtDay(grant.until)}
        </div>
      ) : null}

      <div className="card">
        <div className="card-title">
          {tr('period')} {fmtDay(sel.pinfo.start)}–{fmtDay(sel.pinfo.end)} — {tr('total')}
        </div>

        <div className="rowline dim">
          <span>{tr('expectedWork')}</span>
          <b>{fmtH(expected.hours)} {tr('hours')} ({expected.days} {tr('days')})</b>
        </div>
        <div className="exp-bar">
          <div style={{ width: `${Math.min(100, (workedHours / (expected.hours || 1)) * 100)}%` }} />
        </div>

        <div className="rowline"><span>{tr('dv')}</span><b>{fmtH(calc.hours.dvH)} {tr('hours')}</b></div>
        <div className="rowline"><span>{tr('ot')}</span><b>{fmtH(calc.hours.yv1H)} {tr('hours')}</b></div>
        <div className="rowline"><span>{tr('ot2')}</span><b>{fmtH(calc.hours.yv2H)} {tr('hours')}</b></div>

        {sel.adjSum !== 0 ? (
          <div className="rowline dim top-sep">
            <span>± {tr('adjustments')}</span>
            <b style={{ color: sel.adjSum > 0 ? 'var(--ok)' : 'var(--danger)' }}>
              {sel.adjSum > 0 ? '+' : ''}{fmt(sel.adjSum)}
            </b>
          </div>
        ) : null}

        <div className={`rowline${sel.adjSum !== 0 ? '' : ' top-sep'}`}><span>{tr('gross')}</span><b>{fmt(calc.gross)}</b></div>
        <div className="rowline dim">
          <span>− {tr('pension')} ({settings.pensionEmpPct + (snap.employee.extraPensionPct || 0)}%)</span>
          <b>−{fmt(calc.pension)}</b>
        </div>
        <div className="rowline dim"><span>− {tr('union')} ({calc.unionPctUsed}%)</span><b>−{fmt(calc.union)}</b></div>
        <div className="rowline dim"><span>− {tr('tax')}</span><b>−{fmt(calc.tax)}</b></div>
        <div className="rowline hero"><span>{tr('payout')}</span><b style={{ color: 'var(--primary)' }}>{fmt(calc.net)}</b></div>

        {workedHours > 0 ? (
          <div className="rowline dim" style={{ marginTop: -4, fontSize: 12 }}>
            <span>{tr('avgHourlyNet')}</span>
            <b style={{ fontSize: 12 }}>{fmt(calc.net / workedHours)} / {tr('hours')}</b>
          </div>
        ) : null}

        <div className="rowline dim top-sep"><span>{tr('employerCost')}</span><b>{fmt(calc.totalCost)}</b></div>
      </div>

      <div className="card">
        <div className="card-title">{tr('perDay')}</div>
        {items.length === 0 ? <div className="dimtx" style={{ fontSize: 13 }}>{tr('empty')}</div> : null}
        {items.map((item, i) =>
          item.kind === 'gap' ? (
            <div key={`gap-${i}`} className="dayrow gap">
              <div className="d-date gap-span">{item.span}</div>
              <div className="d-mid dimtx">{item.label}</div>
            </div>
          ) : (
            <div key={item.row.date}>
              <div className="dayrow">
                <div className="d-date">
                  <b>{fmtDay(item.row.date)}</b>
                  <span>{dow(lang, item.row.dow)}</span>
                </div>
                <div className="d-mid">
                  {item.row.sick ? (
                    <span className="chip" style={{ borderColor: 'var(--warn)', color: 'var(--warn)' }}>{tr('sick')}</span>
                  ) : item.row.vacation ? (
                    <span className="chip" style={{ borderColor: 'var(--ok)', color: 'var(--ok)' }}>{tr('vacation')}</span>
                  ) : item.row.h ? (
                    <span>
                      <b>{fmtH(item.row.h)}</b>
                      <span className="dimtx"> klst</span>
                      {item.row.yv1H ? <span className="d-tag" style={{ color: 'var(--live)' }}>YV1 +{fmtH(item.row.yv1H)}</span> : null}
                      {item.row.yv2H ? <span className="d-tag" style={{ color: 'var(--ot2)' }}>YV2</span> : null}
                      {view.editedDates.has(item.row.date) ? <span className="d-tag edited-mark" title={tr('edited')}>✎</span> : null}
                    </span>
                  ) : null}
                </div>
                <div className="d-amt">
                  {item.row.gross ? (
                    <div>
                      <b>{fmt(item.row.gross)}</b>
                      <span>≈ {fmt(item.row.net)} net</span>
                    </div>
                  ) : null}
                </div>
                {canEdit(item.row) ? (
                  <button className="edit-btn" onClick={() => onEditDay(item.row)} aria-label={tr('editHours')}>
                    <Ic.edit size={15} />
                  </button>
                ) : null}
              </div>
              {item.adjustments.map((a) => (
                <div className="dayrow adj-day" key={a.id}>
                  <div className="d-date" />
                  <div className="d-mid" style={{ fontSize: 12 }}>± {tr('adjustments')}{a.note ? ` · ${a.note}` : ''}</div>
                  <div className="d-amt">
                    <b style={{ color: a.amount >= 0 ? 'var(--ok)' : 'var(--danger)' }}>
                      {a.amount >= 0 ? '+' : ''}{fmt(a.amount)}
                    </b>
                  </div>
                </div>
              ))}
            </div>
          ),
        )}
      </div>
    </div>
  );
}
